import { useMemo } from 'react';
import { maskitoNumberOptionsGenerator, maskitoParseNumber } from '@maskito/kit';
import { MaskitoOptions } from '@maskito/core';
import { compact } from '@direct-frontend/stdlib';

import { MaskConfig } from '../../types';
import { createBrandedMaskConfig } from '../../utils';

import { CHAR_MINUS } from './constants';
import {
    createLeadingZeroesValidationPlugin,
    createMinMaxPlugin,
    createNotEmptyIntegerPlugin,
    dropFloatingDecimalSymbolPlugin,
} from './maskitoNumber';
import { createMaskPostfixAdditionals } from './utils';

export interface NumberMaskProps {
    /**
     * Постфикс, добавляющийся в инпуте после числа
     */
    postfix?: string;
    /**
     * Максимальное количество символов после запятой
     * @default 2
     */
    precision?: number;
    /**
     * Минимальное число, доступное для ввода в input
     * @default Number.MIN_SAFE_INTEGER
     */
    min?: number;
    /**
     * Максимальное число, доступное для ввода в input
     * @default Number.MAX_SAFE_INTEGER
     */
    max?: number;
    /**
     * true, если нужен разделитель тысяч в числе
     * @default true
     */
    withThousandSeparator?: boolean;
    /**
     * Символ разделителя тысяч.
     * TODO DIRECT-214054 Необходимо удалить после релиза
     * @default ' '
     */
    customThousandSeparator?: string;
}
/**
 * Хук конфигурации числовых масок
 */
export const useNumberMask = ({
    postfix,
    precision = 2,
    min = Number.MIN_SAFE_INTEGER,
    max = Number.MAX_SAFE_INTEGER,
    withThousandSeparator = true,
    customThousandSeparator = ' ',
}: NumberMaskProps = {}): MaskConfig => {
    const decimalSeparator = ',';
    const thousandSeparator = withThousandSeparator ? customThousandSeparator : '';
    const baseMaskOptions = useMemo(
        () =>
            maskitoNumberOptionsGenerator({
                decimalSeparator,
                min,
                max,
                thousandSeparator,
                maximumFractionDigits: precision,
            }),
        [max, min, precision, thousandSeparator],
    );

    const modifiedMask: MaskitoOptions = useMemo(() => {
        let result = baseMaskOptions;

        result = {
            ...result,
            plugins: [
                dropFloatingDecimalSymbolPlugin(decimalSeparator),
                createLeadingZeroesValidationPlugin(decimalSeparator, thousandSeparator),
                createNotEmptyIntegerPlugin(decimalSeparator),
                createMinMaxPlugin({ min, max, decimalSeparator }),
            ],
        };

        if (postfix) {
            const postfixAdditionals = createMaskPostfixAdditionals(postfix);
            result = {
                ...result,
                preprocessors: compact([postfixAdditionals.preprocessor, ...result.preprocessors]),
                postprocessors: compact([...result.postprocessors, postfixAdditionals.postprocessor]),
                plugins: compact([...result.plugins, postfixAdditionals.plugin]),
            };
        }

        return result;
    }, [baseMaskOptions, max, min, postfix, thousandSeparator]);

    const result = useMemo(
        () =>
            createBrandedMaskConfig({
                maskitoOptions: modifiedMask,
                transformToUnmasked: value => {
                    const trimmedValue = value.trim();

                    if (!trimmedValue) {
                        return '';
                    }

                    // Когда в value лежит только минус, определяем как 0
                    const fixedValue = trimmedValue === CHAR_MINUS ? '0' : trimmedValue;

                    const parsedNumber = maskitoParseNumber(fixedValue, decimalSeparator);

                    // В инпуте не валидное число, что аналогично не введенному числу
                    if (isNaN(parsedNumber)) {
                        return '';
                    }

                    return String(parsedNumber);
                },
            }),
        [modifiedMask],
    );

    return result;
};
